// Center announcement component for game state changes

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * @typedef {'combat-start' | 'combat-end' | 'pc-turn' | 'npc-turn'} AnnouncementType
 */

/**
 * Center announcement component
 * Displays temporary announcements in the center of the screen
 */
class CenterAnnouncement extends BaseComponent {
  static properties = {
    message: { type: String },
    type: { type: String },
    duration: { type: Number },
    _visible: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .center-announcement {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 32px;
      font-weight: bold;
      padding: 16px 32px;
      border-radius: 12px;
      text-align: center;
      z-index: 2000;
      pointer-events: none;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
      white-space: nowrap;
      animation: announcementEnter 0.3s ease-out;
    }

    .center-announcement.is-leaving {
      animation: announcementLeave 0.5s ease-in forwards;
    }

    .is-combat-start {
      background: rgba(185, 28, 28, 0.9);
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    .is-combat-end {
      background: rgba(22, 101, 52, 0.9);
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    .is-pc-turn {
      background: rgba(59, 130, 246, 0.9);
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    .is-npc-turn {
      background: rgba(127, 29, 29, 0.9);
      color: white;
      border: 2px solid rgba(255, 255, 255, 0.3);
    }

    @keyframes announcementEnter {
      from {
        opacity: 0;
        transform: translate(-50%, -50%) scale(0.8);
      }
      to {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
    }

    @keyframes announcementLeave {
      from {
        opacity: 1;
        transform: translate(-50%, -50%) scale(1);
      }
      to {
        opacity: 0;
        transform: translate(-50%, -50%) scale(1.1);
      }
    }
  `;

  constructor() {
    super();
    this.message = '';
    /** @type {AnnouncementType} */
    this.type = 'pc-turn';
    this.duration = 2000;
    this._visible = false;
    this._isLeaving = false;
    this._timeoutId = null;
  }

  /** @param {Map<string, unknown>} changedProps */
  willUpdate(changedProps) {
    if (changedProps.has('message') && this.message) {
      this._showAnnouncement();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
    }
  }

  _showAnnouncement() {
    this._visible = true;
    this._isLeaving = false;

    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
    }

    this._timeoutId = setTimeout(() => {
      this._isLeaving = true;
      this.requestUpdate();

      // Wait for fade out animation
      setTimeout(() => {
        this._visible = false;
        this._isLeaving = false;
        this.dispatchEvent(new CustomEvent('complete', { bubbles: true }));
      }, 500);
    }, this.duration);
  }

  _getTypeClass() {
    switch (this.type) {
      case 'combat-start':
        return 'is-combat-start';
      case 'combat-end':
        return 'is-combat-end';
      case 'pc-turn':
        return 'is-pc-turn';
      case 'npc-turn':
        return 'is-npc-turn';
      default:
        return 'is-pc-turn';
    }
  }

  render() {
    if (!this._visible) {
      return html``;
    }

    return html`
      <div class="center-announcement ${this._getTypeClass()} ${this._isLeaving ? 'is-leaving' : ''}">
        ${this.message}
      </div>
    `;
  }
}

customElements.define('center-announcement', CenterAnnouncement);
